import * as React from "react";
import { X } from 'lucide-react';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { searchStaffByName } from "../../services/user/User";
import { useApiClient } from "../../context/ApiContext";

interface PersonData {
  text: string;
  secondaryText: string;
  tertiaryText: string;
  role: string;
  region: string;
}

export interface SelectedPerson {
  loginName: string;
  fullName: string;
  role: string;
  region: string;
}

interface PBSPeoplePickerProps {
  label?: string;
  required?: boolean;
  labelOnLeft?: boolean;
  itemLimit?: number;
  criteria?: any;
  onChange: (loginNames: string) => void;
  onSelectionChange?: (persons: SelectedPerson[]) => void;
}

export function PBSPeoplePicker(props: PBSPeoplePickerProps) {
  const [searchValue, setSearchValue] = React.useState("");
  const [selectedPeople, setSelectedPeople] = React.useState<PersonData[]>([]);
  const [suggestions, setSuggestions] = React.useState<PersonData[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isOpen, setIsOpen] = React.useState(false);
  const apiClient = useApiClient();
  const containerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const stripDomainFromLoginName = (loginName: string): string => {
    // Remove domain prefix (DOMAIN\username) or suffix (username@domain.com)
    if (loginName.includes('\\')) {
      return loginName.split('\\')[1];
    }
    if (loginName.includes('@')) {
      return loginName.split('@')[0];
    }
    return loginName;
  };

  const searchPeople = React.useCallback(
    async (filterText: string) => {
      if (filterText && filterText.length > 2) {
        setIsLoading(true);
        setIsOpen(true);
        try {
          const resultList = await searchStaffByName(apiClient, filterText);
          const people: PersonData[] = resultList.data.value
            ? resultList.data.value.map((item: any) => ({
                text: item.FullName,
                secondaryText: `${item.Role}, ${item.Region}`,
                tertiaryText: stripDomainFromLoginName(item.LoginName),
                role: item.Role,
                region: item.Region,
              }))
            : [];
          const filteredPeople = people.filter(
            (person) => !selectedPeople.some((selected) => selected.tertiaryText === person.tertiaryText)
          );
          setSuggestions(filteredPeople);
        } catch (error) {
          console.error("Error searching people:", error);
          setSuggestions([]);
        } finally {
          setIsLoading(false);
        }
      } else {
        setSuggestions([]);
        setIsOpen(false);
      }
    },
    [apiClient, selectedPeople]
  );

  const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchValue(value);
    searchPeople(value);
  };

  const toSelectedPersons = (people: PersonData[]): SelectedPerson[] =>
    people.map((p) => ({ loginName: p.tertiaryText, fullName: p.text, role: p.role, region: p.region }));

  const onPersonSelect = (person: PersonData) => {
    if (props.itemLimit !== undefined && selectedPeople.length >= props.itemLimit) return;
    const newSelectedPeople = [...selectedPeople, person];
    setSelectedPeople(newSelectedPeople);
    setSearchValue("");
    setSuggestions([]);
    setIsOpen(false);
    props.onChange(newSelectedPeople.map((p) => p.tertiaryText).join(","));
    props.onSelectionChange?.(toSelectedPersons(newSelectedPeople));
  };

  const onPersonRemove = (personToRemove: PersonData) => {
    const newSelectedPeople = selectedPeople.filter(
      (person) => person.tertiaryText !== personToRemove.tertiaryText
    );
    setSelectedPeople(newSelectedPeople);
    props.onChange(newSelectedPeople.map((p) => p.tertiaryText).join(","));
    props.onSelectionChange?.(toSelectedPersons(newSelectedPeople));
  };

  const getInitials = (name: string) =>
    name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);

  const showDropdown = isOpen && (isLoading || suggestions.length > 0 || searchValue.length > 2);

  return (
    <div className={props.labelOnLeft ? "flex flex-row items-center gap-2.5" : "flex flex-col gap-2"}>
      {props.label && (
        <Label>
          {props.label}
          {props.required && <span className="text-destructive ml-0.5"> *</span>}
        </Label>
      )}

      <div ref={containerRef} className="relative">
        <Input
          aria-label="People Picker"
          placeholder="Type in a few letters of name"
          value={searchValue}
          onChange={onInputChange}
        />

        {showDropdown && (
          <div className="absolute z-50 w-full bg-popover border border-border rounded-md shadow-md max-h-[200px] overflow-y-auto top-full mt-1">
            {isLoading && (
              <div className="p-2 text-sm text-muted-foreground">Loading...</div>
            )}
            {!isLoading && suggestions.length === 0 && searchValue.length > 2 && (
              <div className="p-2 text-sm text-muted-foreground">No results found</div>
            )}
            {suggestions.map((person) => (
              <button
                key={person.tertiaryText}
                type="button"
                className="w-full flex items-center gap-2 p-2 text-left hover:bg-muted text-sm cursor-pointer border-none bg-transparent"
                onClick={() => onPersonSelect(person)}
              >
                <Avatar className="h-8 w-8 flex-none">
                  <AvatarFallback className="text-xs">{getInitials(person.text)}</AvatarFallback>
                </Avatar>
                <div>
                  <div className="font-semibold">{person.text}</div>
                  <div className="text-xs text-muted-foreground">{person.secondaryText}</div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {selectedPeople.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1">
          {selectedPeople.map((person) => (
            <Badge key={person.tertiaryText} variant="secondary" className="gap-1 pr-1">
              <Avatar className="h-4 w-4">
                <AvatarFallback className="text-[8px]">{getInitials(person.text)}</AvatarFallback>
              </Avatar>
              <span>{person.text}</span>
              <button
                type="button"
                className="ml-1 rounded-full hover:bg-muted-foreground/20 p-0.5"
                onClick={() => onPersonRemove(person)}
                aria-label={`Remove ${person.text}`}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
